import { nextTick, onMounted, onUnmounted, ref } from "vue";
import { useSystemSocketStore } from "@/store/systemSocket";
import {
  DailyTrend,
  IContractDate,
  MarketDisplayItemContract as MainModel,
  MarketDisplayItemInstrument,
  MtmViewModel,
  PublishAll,
} from "@/models/marketData";
import { createTypedArray } from "@/utils/useWebsocket";
import { useMTMStore } from "@/store/mtm";
import { useToastStore } from "@/store/toastStore";
import { useContractDateStore } from "@/store/contractDate";
import useConsoleLogger from "./useConsoleLogger";
import { SocketResponse } from "@/models/trading";
import { useTradeHistory } from "@/store/tradeHistory";
import { HubConnection } from "@microsoft/signalr";
import { useMarketDisplayStore } from "@/store/marketDisplay";
import { useInstrumentStore } from "@/store/instruments";

export const useSystemSocket = () => {
  const systemSocketStore = useSystemSocketStore();
  const toastStore = useToastStore();

  const initialize = async () => {
    if (systemSocketStore.isHotReload || !systemSocketStore.socket) {
      await systemSocketStore.initializeSocket(async () => {
        const socket = systemSocketStore.socket as HubConnection | null;
        await socket?.invoke("SubscribeToDailyTrend");

        return Promise.resolve();
      }, [
        {
          name: "MTMInit",
          func: handleMTMUpdate,
        },
        {
          name: "ContractDateInit",
          func: handleContractDatesUpdates,
        },
        {
          name: "InstrumentsInit",
          func: handleInstrumentUpdates,
        },
        {
          name: "DailyTrendsInit",
          func: handleDailyTrendUpdates,
        },
        {
          name: "DailyTrendUpdate",
          func: handleDailyTrendUpdates,
        },
        {
          name: "DownloadsCompleted",
          func: handleSystemReady,
        },
      ]);
    }
  };
  const handleDisconnectCheck = async () => {
    const socket = systemSocketStore.socket as HubConnection | null;
    if (socket?.state == "Connected") {
      const res = await socket?.invoke<SocketResponse>(
        "DisconnectCheck"
      );
      //boolean fails for some reason
      const temp = res?.Message == "True" ? true : false;
      return temp;
    }
    return false;
  };

  onUnmounted(async () => {
    // await systemSocketStore.socket?.invoke<Socket>("UnsubscribeFromDailyTrend");
    console.log()
    systemSocketStore.closeSocket();
  });
  // Phase 5: RAF batching for daily trend updates.
  // DailyTrendUpdate fires during the session as trades complete; batching ensures
  // all updates within a frame land together rather than triggering one Vue cycle per item.
  const _pendingTrends: DailyTrend[] = [];
  let _trendRafScheduled = false;

  const handleDailyTrendUpdates = (message: string) => {
    try {
      const temp = createTypedArray<DailyTrend>(message);
      _pendingTrends.push(...temp);
      if (!_trendRafScheduled) {
        _trendRafScheduled = true;
        requestAnimationFrame(() => {
          _trendRafScheduled = false;
          const batch = _pendingTrends.splice(0);
          const store = useTradeHistory();
          batch.forEach((e) => store.updateItem(e));
        });
      }
    } catch (err) {
      useConsoleLogger.error("error parsing daily trend-", message, err);
    }
  };
  function batchUpdateItems(updatedItems: MainModel[]) {
    const store = useMarketDisplayStore();
    store().$patch((state: { data: MainModel[] }) => {
      updatedItems.forEach((updatedItem) => {
        const index = state.data.findIndex(
          (e: MainModel) => e["contract"] === updatedItem["contract"]
        );
        if (index === -1) {
          // console.log("Push items")
          state.data.push(updatedItem);
        } else {
          // Assuming deepMerge is a utility function for deep merging objects
          store().deepMerge(
            state.data[index],
            updatedItem,
            "systemSocketBatch"
          );
        }
      });
    });
  }
  const loadMarketDataChunks = async () => {
    const socket = systemSocketStore.socket as HubConnection | null;
    if (
      socket &&
      socket.state == "Connected"
    ) {
      socket.on("MarketDisplayCompleted", (msg: string) => {
        socket.off("MarketDisplayChunk");
        socket.off("MarketDisplayCompleted");
        console.log("Expecting completed message: ", msg);
        return Promise.resolve();
      });
      socket.on("MarketDisplayChunk", (msg: string | object[]) => {
        const temp = createTypedArray<MainModel>(msg);
        batchUpdateItems(temp);
      });
      console.log("Invoke chunking");
      await socket.invoke<SocketResponse>(
        "GetMarketDisplayChunks"
      );
    }
  };
  const loadDailyTrend = async () => {
    const socket = systemSocketStore.socket as HubConnection | null;
    if (
      socket &&
      socket.state == "Connected"
    ) {
      const promises = [
        socket.invoke<SocketResponse>(
          "PublishAllData",
          PublishAll.DailyTrends,
          null
        ),
      ];
      return await Promise.all(promises);
    }
    return Promise.resolve();
  };
  const handleMTMUpdate = (message: string) => {
    const mtm = useMTMStore("useSystemSocket", []);
    try {
      const temp = createTypedArray<MtmViewModel>(message);
      temp.forEach((e) => {
        mtm.store.updateItem(e);
      });
      useConsoleLogger.log(
        "Parsed MTM update : ",
        temp.length,
        temp,
        mtm.store.data.length
      );
      toastStore.addToast("success", `Added ${temp.length} mtm`);
      //
    } catch (err) {
      useConsoleLogger.error("error parsing MTMInit", message, err);
      return Promise.reject(err);
    }
  };
  const handleSystemReady = (val: any) => {
    const de = val == "True" ? true : false;
    if (de == true) {
      systemSocketStore.isBackendReadyForTrading = true;
    }
  };
  const loadMTM = async () => {
    const socket = systemSocketStore.socket as HubConnection | null;
    if (
      socket &&
      socket.state == "Connected"
    ) {
      const promises = [
        socket.invoke<SocketResponse>(
          "PublishAllData",
          PublishAll.MTM,
          null
        ),
      ];
      return await Promise.all(promises);
    }
  };
  const handleContractDatesUpdates = (message: string) => {
    const contracts = useContractDateStore();
    try {
      const temp = createTypedArray<IContractDate>(message);
      temp.forEach((e) => {
        contracts.updateItem(e);
      });
      useConsoleLogger.log("CONTRACT DATE: Parsed update : ", temp);
      toastStore.addToast("success", `Added ${temp.length} contract dates`);
      //
    } catch (err) {
      useConsoleLogger.error("error parsing ContractDateInit", message, err);
      return Promise.reject(err);
    }
  };
  const loadContractDates = async () => {
    const socket = systemSocketStore.socket as HubConnection | null;
    if (
      socket &&
      socket.state == "Connected"
    ) {
      const promises = [
        socket.invoke<SocketResponse>(
          "PublishAllData",
          PublishAll.ContractDate,
          null
        ),
      ];
      return await Promise.all(promises);
    }
  };
  const handleInstrumentUpdates = (message: string) => {
    const instruments = useInstrumentStore();
    try {
      const temp = createTypedArray<MarketDisplayItemInstrument>(message);
      temp.forEach((e) => {
        instruments.updateItem(e);
      });
      useConsoleLogger.log("INSTRUMENT: Parsed update : ", temp);
      toastStore.addToast("success", `Added ${temp.length} instruments`);
      //
    } catch (err) {
      useConsoleLogger.error("error parsing InstrumentsInit", message, err);
      return Promise.reject(err);
    }
  };
  const loadInstruments = async () => {
    const socket = systemSocketStore.socket as HubConnection | null;
    if (
      socket &&
      socket.state == "Connected"
    ) {
      const promises = [
        socket.invoke<SocketResponse>(
          "PublishAllData",
          PublishAll.Instruments,
          null
        ),
      ];
      return await Promise.all(promises);
    }
  };
  return {
    systemSocketStore,
    loadMTM,
    loadContractDates,
    loadInstruments,
    loadDailyTrend,
    initialize,
    loadMarketDataChunks,
    handleDisconnectCheck,
    batchUpdateItems
  };
};
